import http from '@utils/http';
import axios from 'axios';

/**
 * Register/Map employee RFID card static UID
 * POST /attendance/public/rfid/register
 * @param {{ userId: string, rfid: string }} payload
 * @param {string} tenantId - Active tenant ID for validation
 */
export const registerEmployeeRfid = (payload, tenantId) => {
    return http.post('/attendance/public/rfid/register', payload, {
        headers: {
            'x-tenant-id': tenantId,
        },
    }).then((res) => res.data?.data ?? res.data);
};

/**
 * Register/Map employee fingerprint thumb ID
 * POST /attendance/public/thumb/register
 * @param {{ userId: string, thumbId: string }} payload
 * @param {string} tenantId - Active tenant ID for validation
 */
export const registerEmployeeThumb = (payload, tenantId) => {
    return http.post('/attendance/public/thumb/register', payload, {
        headers: {
            'x-tenant-id': tenantId,
        },
    }).then((res) => res.data?.data ?? res.data);
};

/**
 * Start biometric fingerprint enrollment session on the VPS backend
 * POST /attendance/public/enroll/start
 * @param {{ userId: string, slotId: number }} payload
 */
export const startFingerprintEnroll = (payload) => {
    return http.post('/attendance/public/enroll/start', payload).then((res) => res.data?.data ?? res.data);
};

/**
 * Get real-time fingerprint enrollment progress from the VPS backend
 * GET /attendance/public/enroll/status?userId=xxx
 * @param {string} userId
 */
export const getFingerprintEnrollStatus = (userId) => {
    return http.get(`/attendance/public/enroll/status?userId=${userId}`).then((res) => res.data?.data ?? res.data);
};

// ==========================================
// ESP32 Direct Local Device Communication
// ==========================================

/**
 * Trigger the physical sensor on the ESP32 local server directly.
 * GET http://<esp32-local-ip>/enroll-finger?id=<slotId>
 * @param {string} esp32LocalIp
 * @param {number} slotId
 */
export const triggerEsp32Enroll = (esp32LocalIp, slotId) => {
    // Clear http/https prefix if present in the stored IP, standardizing to http
    const cleanIp = esp32LocalIp.replace(/^(https?:\/\/)?/, '');
    return axios.get(`http://${cleanIp}/enroll-finger`, {
        params: { id: slotId },
        timeout: 5000,
    }).then((res) => res.data);
};

/**
 * Ask the ESP32 to scan an RFID card and return the UID.
 * The device enters IDLE mode and waits up to 10 seconds for a card swipe.
 * GET http://<esp32-local-ip>/enroll-rfid
 * @param {string} esp32LocalIp
 * @returns {Promise<{ success: boolean, rfid_uid: string }>}
 */
export const triggerEsp32RfidScan = (esp32LocalIp) => {
    const cleanIp = esp32LocalIp.replace(/^(https?:\/\/)?/, '');
    return axios.get(`http://${cleanIp}/enroll-rfid`, {
        timeout: 12000, // 10s device timeout + 2s network buffer
    }).then((res) => res.data);
};


/**
 * Fetch ESP32 physical device diagnostic readings
 * GET http://<esp32-local-ip>/status
 * @param {string} esp32LocalIp
 */
export const getEsp32Status = (esp32LocalIp) => {
    const cleanIp = esp32LocalIp.replace(/^(https?:\/\/)?/, '');
    return axios.get(`http://${cleanIp}/status`, {
        timeout: 5000,
    }).then((res) => res.data);
};

/**
 * Remotely delete a biometric fingerprint slot template from the device
 * GET http://<esp32-local-ip>/delete-finger?id=<slotId>
 * @param {string} esp32LocalIp
 * @param {number} slotId
 */
export const deleteEsp32FingerprintSlot = (esp32LocalIp, slotId) => {
    const cleanIp = esp32LocalIp.replace(/^(https?:\/\/)?/, '');
    return axios.get(`http://${cleanIp}/delete-finger`, {
        params: { id: slotId },
        timeout: 5000,
    }).then((res) => res.data);
};

/**
 * Configure the ESP32 device wirelessly over the local WiFi network (OTA).
 * The device will save the new credentials to flash and reboot automatically.
 * GET http://<esp32-local-ip>/configure?ssid=<ssid>&pass=<pass>&url=<url>
 * @param {string} esp32LocalIp
 * @param {{ ssid?: string, pass?: string, url?: string }} config
 */
export const configureEsp32OverWifi = (esp32LocalIp, { ssid = '', pass = '', url = '' }) => {
    const cleanIp = esp32LocalIp.replace(/^(https?:\/\/)?/, '');
    return axios.get(`http://${cleanIp}/configure`, {
        params: {
            ...(ssid ? { ssid } : {}),
            ...(pass ? { pass } : {}),
            ...(url ? { url } : {}),
        },
        timeout: 8000,
    }).then((res) => res.data);
};

/**
 * Save WiFi credentials to the ESP32 over local WiFi.
 * The device will reboot automatically to connect to the new network.
 * GET http://<esp32-local-ip>/configure-wifi?ssid=<ssid>&pass=<pass>
 */
export const configureEsp32Wifi = (esp32LocalIp, { ssid, pass = '' }) => {
    const cleanIp = esp32LocalIp.replace(/^(https?:\/\/)?/, '');
    return axios.get(`http://${cleanIp}/configure-wifi`, {
        params: {
            ssid,
            ...(pass ? { pass } : {}),
        },
        timeout: 8000,
    })
    .then((res) => res.data)
    .catch((err) => {
        // Fallback if the device is running old firmware (where /configure-wifi does not exist)
        if (err.response?.status === 404) {
            console.log('Falling back to unified /configure endpoint');
            return configureEsp32OverWifi(esp32LocalIp, { ssid, pass });
        }
        throw err;
    });
};

/**
 * Save backend server URL to the ESP32 over local WiFi.
 * No reboot needed — takes effect immediately.
 * GET http://<esp32-local-ip>/configure-server?url=<url>
 */
export const configureEsp32ServerUrl = (esp32LocalIp, url) => {
    const cleanIp = esp32LocalIp.replace(/^(https?:\/\/)?/, '');
    return axios.get(`http://${cleanIp}/configure-server`, {
        params: { url },
        timeout: 5000,
    })
    .then((res) => res.data)
    .catch((err) => {
        // Fallback if the device is running old firmware (where /configure-server does not exist)
        if (err.response?.status === 404) {
            console.log('Falling back to unified /configure endpoint');
            return configureEsp32OverWifi(esp32LocalIp, { url });
        }
        throw err;
    });
};

/**
 * Fetch last active ESP32 device IP and backend server local IP
 * GET /attendance/public/device/info
 */
export const getDeviceNetworkInfo = () => {
    return http.get('/attendance/public/device/info').then((res) => res.data?.data ?? res.data);
};

/**
 * Save detected/configured device IP to backend memory
 * POST /attendance/public/device/ip
 */
export const saveDeviceIpToBackend = (ip) => {
    return http.post('/attendance/public/device/ip', { ip }).then((res) => res.data);
};

